"use client";

import { debounce } from "lodash";
import { Search, X } from "lucide-react";
import Image from "next/image";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import * as m from "@/paraglide/messages";
import { getAssets } from "@/services/controllers/assets";

import { Asset } from "./types";

export default function SearchCoinFilter({
  onSelectCoin,
}: {
  onSelectCoin: React.Dispatch<string>;
}) {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [suggestions, setSuggestions] = useState<Asset[]>([]);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedCoin, setSelectedCoin] = useState<Asset>();

  const resetCoin = useCallback(() => {
    setSelectedCoin(undefined);
    const input: any = document.getElementById("orders-search-input");
    input.value = "";
    setSearchTerm("");
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("reset-orders", resetCoin);
    }

    return () => {
      window.removeEventListener("reset-orders", resetCoin);
    };
  }, [resetCoin]);

  useEffect(() => {
    const controller = new AbortController();
    if (searchTerm.length >= 3) {
      setLoading(true);
      setSuggestions([]);
      getAssets({
        signal: controller.signal,
        params: {
          search: searchTerm,
          limit: 30,
          offset: 0,
        },
      })
        .then((response) => {
          setSuggestions(response.data.data);
        })
        .finally(() => {
          setLoading(false);
        });
    }

    return () => {
      controller.abort();
    };
  }, [searchTerm]);

  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSelectedCoin(undefined);
    const term = e.target.value;
    setSearchTerm(term);
    if (term.length >= 2) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  }, []);
  const handleSuggestionClick = useCallback(
    (suggestion: Asset) => {
      setSelectedCoin(suggestion);
      const input: any = document.getElementById("orders-search-input");
      input.value = suggestion.asset_data.name;
      setShowDropdown(false);
      onSelectCoin(suggestion.asset_name);
    },
    [onSelectCoin],
  );

  const debouncedSearch = useMemo(() => {
    return debounce(handleSearch, 250);
  }, [handleSearch]);

  const onReset = useCallback(() => {
    const input: any = document.getElementById("orders-search-input");
    input.value = "";
    setSearchTerm("");
    setSuggestions([]);
    setSelectedCoin(undefined);
    setShowDropdown(false);
  }, []);

  return (
    <div className="relative w-full lg:max-w-xs">
      <div className="relative felx items-center">
        {searchTerm !== "" ? (
          <X
            className="w-4 h-auto absolute right-0 me-10 top-[30%] cursor-pointer"
            onClick={onReset}
          />
        ) : (
          <></>
        )}
        {selectedCoin?.id != null ? (
          <Image
            src={selectedCoin.asset_data.image ?? "/placeholder.svg"}
            width={30}
            height={30}
            alt="placeholder"
            className="w-auto h-[75%] absolute inset-y-1 left-0 pl-3"
          />
        ) : (
          <></>
        )}
        <Input
          id="orders-search-input"
          placeholder={m.search() + "..."}
          onChange={debouncedSearch}
          className={twMerge(
            selectedCoin?.id != null ? "ps-12" : "",
            "w-full pr-10",
          )}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
      </div>
      {showDropdown && (
        <div className="absolute z-10 w-full bg-white rounded-sm shadow-lg mt-2 dark:bg-gray-800 max-h-[300px] overflow-y-auto">
          <ul className="py-2 grid gap-4 p-4">
            {loading
              ? [...Array(10)].map(() => (
                  <div
                    className="flex gap-4 w-full items-center"
                    key={Math.random() * 10}
                  >
                    <Skeleton className="min-w-10 min-h-10" />
                    <Skeleton className="w-full h-5" />
                  </div>
                ))
              : suggestions.map((suggestion) => (
                  <li
                    key={suggestion.id}
                    className="cursor-pointer hover:bg-muted flex gap-4 items-center"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <Image
                      src={
                        suggestion.asset_data.image.startsWith("https:")
                          ? suggestion.asset_data.image
                          : "/placeholder.svg"
                      }
                      alt={suggestion.asset_name}
                      width={40}
                      height={40}
                      className="rounded-sm object-contain w-10 h-10 aspect-square py-1"
                    />
                    <p className=" text-left font-medium text-sm">
                      {suggestion.asset_data.name}
                    </p>
                  </li>
                ))}
          </ul>
        </div>
      )}
    </div>
  );
}
